import React, { useEffect, useState } from "react";
import api from "../../config/axios";
import Header from "../common/Header";
import { useNavigate } from "react-router-dom";
import "./MyReservations.css";
import "./ReservedTableList.css";

interface Table {
  _id: string;
  name: string;
  seats: number;
}
interface Reservation {
  _id: string;
  table: Table;
  customerName: string;
  customerPhone: string;
  peopleCount: number;
  time: string;
  note?: string;
  status: string;
  reservedItems?: any[];
}

interface MyReservationsProps {
  user: { username: string; role: string };
  onLogout: () => void;
}

const statusText: Record<string, string> = {
  pending: "Chờ xác nhận",
  reserved: "Đã đặt",
  'in-use': "Đang sử dụng",
  completed: "Hoàn thành",
  cancelled: "Đã hủy",
  expired: "Quá hạn"
};

const MyReservations: React.FC<MyReservationsProps> = ({ user, onLogout }) => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any | null>(null);

  useEffect(() => {
    api.get("/api/tables/my-reservations")
      .then(res => setReservations(res.data))
      .catch(() => setError("Không thể tải danh sách đặt bàn"))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => n < 10 ? '0' + n : n;
    return `${pad(d.getHours())}:${pad(d.getMinutes())}, ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  };

  const handleCancelReservation = async (id: string) => {
    if (!window.confirm("Bạn có chắc muốn hủy đơn đặt bàn này?")) return;
    try {
      await api.patch(`/api/tables/reservations/${id}`, { status: "cancelled" });
      setReservations(rs => rs.map(r => r._id === id ? { ...r, status: "cancelled" } : r));
    } catch (err) {
      alert("Không thể hủy đặt bàn. Vui lòng thử lại!");
    }
  };

  const handleRowClick = (reservation: any) => {
    setSelectedReservation(reservation);
    setShowModal(true);
  };

  return (
    <div className="reserved-table-list-container">
      <Header user={user} onLogout={onLogout} />
      <div className="reserved-table-content-wrapper">
        <div className="reserved-table-header">
          <button className="back-btn" onClick={() => navigate(-1)} title="Quay lại">←</button>
          <h2 className="reserved-table-title">ĐƠN ĐẶT BÀN CỦA TÔI</h2>
        </div>
        <div className="reserved-table-content">
        {loading ? <div>Đang tải...</div> : error ? <div style={{ color: 'red' }}>{error}</div> : (
          <div className="reserved-table-scroll">
            <table className="reserved-table">
              <thead>
                <tr>
                  <th>Bàn</th>
                  <th>Số người</th>
                  <th>Thời gian</th>
                  <th>Ghi chú</th>
                  <th>Trạng thái</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map(rsv => (
                  <tr key={rsv._id} className={`my-reservations-row ${rsv.status}`} onClick={() => handleRowClick(rsv)} style={{ cursor: 'pointer' }}>
                    <td>{rsv.table?.name}</td>
                    <td>{rsv.peopleCount}</td>
                    <td>{formatDate(rsv.time)}</td>
                    <td>{rsv.note}</td>
                    <td className="my-reservations-status">{statusText[rsv.status] || rsv.status}</td>
                    <td>
                      {(rsv.status === "pending" || rsv.status === "reserved") && (
                        <button className="reservation-action-btn cancel" onClick={e => { e.stopPropagation(); handleCancelReservation(rsv._id); }}>Hủy</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {reservations.length === 0 && !loading && <div className="my-reservations-empty">Bạn chưa có đơn đặt bàn nào.</div>}
        </div>
      </div>
      {showModal && selectedReservation && (
        <div className="reservation-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="reservation-modal" onClick={e => e.stopPropagation()}>
            <h3>Chi tiết món đã đặt</h3>
            <p><b>Bàn:</b> {selectedReservation.table?.name}</p>
            <p><b>Khách:</b> {selectedReservation.customerName}</p>
            {selectedReservation.reservedItems && selectedReservation.reservedItems.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Ảnh</th>
                    <th>Tên món</th>
                    <th>Số lượng</th>
                    <th>Đơn giá</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedReservation.reservedItems.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td>
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                        ) : (
                          <span style={{ color: '#aaa' }}>Không có</span>
                        )}
                      </td>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{item.price?.toLocaleString()}đ</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>Chưa có món nào được đặt cho bàn này.</p>
            )}
            <button className="close-btn" onClick={() => setShowModal(false)}>Đóng</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyReservations; 